import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Disclosure, SupportedLanguage } from "src/components/disclosures/disclosure.types";
import { DEFAULT_LANGUAGE } from "src/components/disclosures/disclosures.config";
import { useUserSlice } from "src/store/features/user.slice";
import {
  useGetTodayDisclosureAcceptanceQuery,
  type GetTodayDisclosureAcceptanceApiArg,
} from "src/store/features/api/generated";
import { useLegalDisclosure } from "./useLegalDisclosure";

/** Transaction type for daily disclosure acceptance (trade, target_order, dca_strategy, withdrawals). */
export type DisclosureAcceptanceTransactionType =
  GetTodayDisclosureAcceptanceApiArg["transactionType"];

/** Metadata captured during the disclosure acceptance flow */
export interface DisclosureMetadata {
  /** ISO 8601 date/time when the disclosure was displayed to the user */
  disclosureRequestDateTime: string;
  /** ISO 8601 date/time when the user accepted the disclosure */
  disclosureAcceptanceDateTime: string;
}

interface UseTransactionDisclosureReturn {
  /** Whether the user requires a transaction disclosure (a published doc applies to their jurisdiction) */
  requiresDisclosure: boolean;
  /** Whether the disclosure document or today's acceptance check is still resolving. While true, `requiresDisclosure === false` means "unknown", not "not required". */
  isLoading: boolean;
  /** Whether the disclosure modal is open */
  isOpen: boolean;
  /** The transaction disclosure to display */
  transactionDisclosure: Disclosure | null;
  /** The currently selected language */
  selectedLanguage: SupportedLanguage;
  /** Handler to open the modal */
  openModal: () => void;
  /** Handler to close the modal */
  closeModal: () => void;
  /** Handler to accept the disclosure */
  handleAccept: (disclosureId: string) => void;
  /** Handler to change the language */
  handleLanguageChange: (language: SupportedLanguage) => void;
  /** Modal component props for easy rendering */
  modalProps: {
    open: boolean;
    onClose: () => void;
    disclosure: Disclosure | null;
    selectedLanguage: SupportedLanguage;
    onLanguageChange: (language: SupportedLanguage) => void;
    onAccept: (disclosureId: string) => void;
  };
  /**
   * Execute an action with disclosure check.
   * If the user requires a disclosure and hasn't accepted it, the disclosure modal will open.
   * Once accepted, the action will be executed.
   *
   * @param action - The action to execute after disclosure is accepted
   * @returns A function that triggers the disclosure check and executes the action
   */
  withDisclosureCheck: (action: () => void | Promise<void>) => () => void;
  /** Disclosure metadata (timestamps) captured on acceptance; null once that local day has passed */
  disclosureMetadata: DisclosureMetadata | null;
  /** Use this when building submit payloads: includes timestamps + timezone when user has accepted (either just now in modal or already today from API) */
  disclosurePayload: (DisclosureMetadata & { timezone: string }) | null;
}

/**
 * Custom hook to manage transaction-specific disclosure modals.
 * Shows disclosure before each transaction when a published transaction-disclosure
 * document applies to the user's jurisdiction (resolved server-side).
 *
 * Unlike the account creation disclosures, these transaction disclosures are shown
 * before every transaction (deposit, withdraw, trade, target order, DCA).
 *
 * @param onAcceptCallback - Optional callback function to execute when disclosure is accepted
 * @param transactionType - When set, acceptance is stored and checked per type per day (trade, target_order, dca_strategy, crypto_withdrawal, fiat_withdrawal). Omit for flows like deposit that don't use daily acceptance.
 * @param documentType - Backend LegalDocumentType stream to resolve. Defaults to "transaction-disclosure"
 *   (US state-scoped: trade, target order, DCA, deposit). Withdrawals pass "withdrawal-disclosure",
 *   which resolves state → country → global, so a global doc applies to users without a US state.
 * @returns Object containing modal state, handlers, and props for rendering the TransactionDisclosureModal
 *
 * @example
 * ```tsx
 * const { modalProps, transactionDisclosure, withDisclosureCheck } = useTransactionDisclosure();
 *
 * const handleDeposit = withDisclosureCheck(async () => {
 *   // This will only execute after the user accepts the disclosure (if required)
 *   await processDeposit();
 * });
 *
 * return (
 *   <>
 *     <Button onClick={handleDeposit}>Deposit</Button>
 *     {transactionDisclosure && <TransactionDisclosureModal {...modalProps} />}
 *   </>
 * );
 * ```
 */
const getBrowserTimezone = (): string =>
  typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";

const isSameLocalDay = (isoDateTime: string): boolean =>
  new Date(isoDateTime).toDateString() === new Date().toDateString();

export const useTransactionDisclosure = (
  onAcceptCallback?: (disclosureId: string, metadata?: DisclosureMetadata) => void,
  transactionType?: DisclosureAcceptanceTransactionType,
  documentType: "transaction-disclosure" | "withdrawal-disclosure" = "transaction-disclosure",
): UseTransactionDisclosureReturn => {
  const { address } = useUserSlice();
  const timezone = useMemo(() => getBrowserTimezone(), []);

  const [isOpen, setIsOpen] = useState(false);
  const [transactionDisclosure, setTransactionDisclosure] = useState<Disclosure | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);
  const [pendingAction, setPendingAction] = useState<(() => void | Promise<void>) | null>(null);
  const [disclosureMetadata, setDisclosureMetadata] = useState<DisclosureMetadata | null>(null);
  const requestDateTimeRef = useRef<string | null>(null);

  // transaction-disclosure is US state-scoped (no state → no doc); withdrawal-disclosure falls back to a global doc
  const isAcceptanceCheckSkipped =
    transactionType == null ||
    (documentType === "transaction-disclosure" && !address?.state);

  const {
    data: todayAcceptance,
    isSuccess: isTodayAcceptanceSuccess,
    isError: isTodayAcceptanceError,
  } = useGetTodayDisclosureAcceptanceQuery(
    { timezone, transactionType: transactionType! },
    { skip: isAcceptanceCheckSkipped },
  );

  // Unsettled = "accepted today" unknown, surfaced via isLoading; an errored check re-prompts rather than skips
  const isAcceptanceCheckSettled =
    isAcceptanceCheckSkipped || isTodayAcceptanceSuccess || isTodayAcceptanceError;

  // Backend-served disclosure — jurisdiction resolved server-side from
  // the user; sole source of truth, no static-config fallback.
  const { disclosure: resolvedDisclosure, isLoading: isDisclosureDocLoading } =
    useLegalDisclosure({
      type: documentType,
    });

  const isLoading = isDisclosureDocLoading || !isAcceptanceCheckSettled;

  // Reflect the resolved disclosure into local state
  useEffect(() => {
    setTransactionDisclosure(resolvedDisclosure);
  }, [resolvedDisclosure]);

  // Require disclosure when we have a disclosure and either: no daily check (e.g. deposit) or the settled daily check found no acceptance for today
  const requiresDisclosure =
    transactionDisclosure !== null &&
    (transactionType == null || (isAcceptanceCheckSettled && !todayAcceptance));

  // Session acceptance only counts for the local day it happened on — the modals stay mounted across midnight
  const sessionDisclosureMetadata =
    disclosureMetadata && isSameLocalDay(disclosureMetadata.disclosureAcceptanceDateTime)
      ? disclosureMetadata
      : null;

  // Handle opening the modal
  const openModal = useCallback(() => {
    requestDateTimeRef.current = new Date().toISOString();
    setIsOpen(true);
  }, []);

  // Handle closing the disclosure modal
  const closeModal = useCallback(() => {
    setIsOpen(false);
    setPendingAction(null);
    requestDateTimeRef.current = null;
  }, []);

  // Handle language change
  const handleLanguageChange = useCallback((language: SupportedLanguage) => {
    setSelectedLanguage(language);
  }, []);

  // Handle accepting the disclosure
  const handleAccept = useCallback(
    (disclosureId: string) => {
      const acceptanceDateTime = new Date().toISOString();
      const metadata: DisclosureMetadata = {
        disclosureRequestDateTime: requestDateTimeRef.current ?? acceptanceDateTime,
        disclosureAcceptanceDateTime: acceptanceDateTime,
      };
      setDisclosureMetadata(metadata);
      requestDateTimeRef.current = null;

      // Acceptance is recorded in the backend when the user submits the transaction (trade/target/DCA/withdrawal), not here.

      if (onAcceptCallback) {
        onAcceptCallback(disclosureId, metadata);
      }
      setIsOpen(false);

      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
    },
    [onAcceptCallback, pendingAction],
  );

  // Create a function that wraps an action with disclosure check
  const withDisclosureCheck = useCallback(
    (action: () => void | Promise<void>) => {
      return () => {
        if (requiresDisclosure) {
          // Store the action to execute after acceptance
          setPendingAction(() => action);
          requestDateTimeRef.current = new Date().toISOString();
          setIsOpen(true);
        } else {
          // No disclosure required, execute immediately
          action();
        }
      };
    },
    [requiresDisclosure],
  );

  // Modal props for easy rendering
  const modalProps = {
    open: isOpen,
    onClose: closeModal,
    disclosure: transactionDisclosure,
    selectedLanguage,
    onLanguageChange: handleLanguageChange,
    onAccept: handleAccept,
  };

  // Use for submit payloads: either accepted-today-in-modal or already-accepted-today (API), plus timezone
  const disclosurePayload = useMemo((): (DisclosureMetadata & { timezone: string }) | null => {
    const base = (sessionDisclosureMetadata ?? todayAcceptance ?? null) as DisclosureMetadata | null;
    if (!base) return null;
    return {
      disclosureRequestDateTime: base.disclosureRequestDateTime,
      disclosureAcceptanceDateTime: base.disclosureAcceptanceDateTime,
      timezone,
    };
  }, [sessionDisclosureMetadata, todayAcceptance, timezone]);

  return {
    requiresDisclosure,
    isLoading,
    isOpen,
    transactionDisclosure,
    selectedLanguage,
    openModal,
    closeModal,
    handleAccept,
    handleLanguageChange,
    modalProps,
    withDisclosureCheck,
    disclosureMetadata: sessionDisclosureMetadata,
    disclosurePayload,
  };
};

export default useTransactionDisclosure;
